'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiFileList3Line,
  RiFocus3Line,
  RiLockLine,
  RiRobot2Line,
  RiShieldCheckLine,
  RiUser3Line,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { dur, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Neye dayanıyor" — güven bölümü, beyaz zeminde.
 *
 * Sayfanın buraya kadarki iki bölümü ajanın NE yaptığını gösterdi. Bu noktada
 * okuyucunun aklındaki soru değişiyor: "peki söyledikleri doğru mu?" Reklam
 * hesabını bir sohbete açacak kişi için asıl risk yanlış bir aksiyon değil,
 * GÜVENLE söylenmiş yanlış bir rakam.
 *
 * ── Neden hata durumu GÖSTERİLMİYOR (kullanıcı kararı, 11 Ağu 2026) ───────
 *
 * Bölümün ilk sürümünde bir anahtar vardı: kullanıcı bağlantıyı koparıyor ve
 * ajanın rakam vermeyi reddettiğini görüyordu. Mekanizma doğruydu ama
 * çerçevesi yanlıştı — "Bağlantı koptu" etiketi, uyarı ikonu ve sarı mürekkep
 * birlikte "bu üründe arıza oluyor" diye okunuyordu. Satış sayfasında bir
 * ürünün başarısız hâlini göstermenin bedeli, kazandırdığı güvenden yüksek.
 *
 * İddia kayboldu mu: hayır, yalnızca olumluya çevrildi. Aynı söz artık
 * kaynak satırının kendisi taşıyor — hangi hesap, hangi aralık, ne zaman
 * okundu. Kaynağı olmayan bir rakam yazılmadığı `lead` cümlesinde ve
 * `facts.source` maddesinde yazılı; hata ekranı basmadan.
 *
 * Bu bölüme yeniden bir "başarısız okuma" durumu EKLEME. Kaldırılma gerekçesi
 * yukarıda; ekleyecekse kullanıcı söyler.
 */

const FACTS = ['source', 'context', 'privacy'] as const;

const FACT_ICON: Record<(typeof FACTS)[number], typeof RiFileList3Line> = {
  source: RiFileList3Line,
  context: RiFocus3Line,
  privacy: RiLockLine,
};

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

/* Kart ağır bir yüzey: `soft` yayı (mass 1.1) — sayfadaki diğer ürün
   yüzeyleriyle aynı ağırlıkta girsin diye. */
const cardV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.soft, dur.base) },
};

/* Kartın İÇİ: soru → yanıt → kaynak. Sıra okuma sırası; kaynak satırı en son
   düşüyor çünkü bölümün asıl cümlesi o. */
const partsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.14 } },
};

const partV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const factsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

export function AgentProof() {
  const t = useTranslations('featuresAgent.proof');
  const uid = useId();

  return (
    <section className="proof" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="proof-inner">
            <motion.div className="proof-head" variants={headV} initial={false} animate="show">
              <motion.p className="proof-eyebrow" variants={copyV}>
                {t('eyebrow')}
              </motion.p>
              <motion.h2 className="proof-title" id={`${uid}-title`} variants={copyV}>
                {t.rich('title', { hl })}
              </motion.h2>
              <motion.p className="proof-lead" variants={copyV}>
                {t('lead')}
              </motion.p>
            </motion.div>

            <motion.div className="proof-card" variants={cardV} initial={false} animate="show">
              <motion.div variants={partsV}>
                <motion.p className="proof-q" variants={partV}>
                  <span className="proof-who">
                    <RiUser3Line size={14} aria-hidden="true" />
                  </span>
                  {t('ok.q')}
                </motion.p>

                <motion.p className="proof-a-text" variants={partV}>
                  <span className="proof-who">
                    <RiRobot2Line size={14} aria-hidden="true" />
                  </span>
                  {t('ok.a')}
                </motion.p>

                {/* Kaynak satırı yanıtın parçası, dipnot değil: bu üründe
                    rakamın nereden geldiği rakam kadar önemli. */}
                <motion.p className="proof-source" variants={partV}>
                  <RiShieldCheckLine size={14} aria-hidden="true" />
                  {t('ok.source')}
                </motion.p>
              </motion.div>
            </motion.div>

            <motion.ul className="proof-facts" variants={factsV} initial={false} animate="show">
              {FACTS.map((key) => {
                const Icon = FACT_ICON[key];
                return (
                  <motion.li key={key} className="proof-fact" variants={copyV}>
                    <Icon className="proof-fact-icon" size={20} aria-hidden="true" />
                    <h3 className="proof-fact-title">{t(`facts.${key}.title`)}</h3>
                    <p className="proof-fact-text">{t(`facts.${key}.text`)}</p>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>
        </div>
      </div>
    </section>
  );
}
